import React, { useEffect, useState, useRef, Component } from 'react';
import { ArrowRight, TrendingUp, Users, Heart, MessageCircle, Target, Zap, Star, Activity, AlertTriangle, Lightbulb, Brain, TrendingDown, CornerRightUp, CornerRightDown, Sparkles, Mail, Send, Phone, ChevronDown, X, Check, Loader2 } from 'lucide-react';
import { insertSignup, insertEnterpriseLead } from '../../lib/supabase';
// Prompt type: 'revele' for insights/data, 'propose' for suggestions/actions
type PromptType = 'revele' | 'propose';
interface Prompt {
  text: string;
  type: PromptType;
}
const PROMPTS: Prompt[] = [{
  text: 'Thomas manque de reconnaissance, ce qui dégrade la qualité de son accueil client.',
  type: 'revele'
}, {
  text: 'Félicitez Thomas plus souvent pour dynamiser son excellence relationnelle.',
  type: 'propose'
}, {
  text: 'Nadia cumule 5 fermetures consécutives, son niveau de fatigue atteint un seuil critique.',
  type: 'revele'
}, {
  text: "Répartissez les fermetures sur deux équipes tournantes pour préserver l'énergie de Nadia.",
  type: 'propose'
}, {
  text: "Alex n'a pas pris la parole en réunion depuis 2 semaines, son engagement chute de 60%.",
  type: 'revele'
}, {
  text: 'Planifiez un échange informel avec Alex autour de 3 questions pour recréer du lien...',
  type: 'propose'
}, {
  text: "L'épuisement de Louis génère des erreurs d'étiquetage répétées en magasin.",
  type: 'revele'
}, {
  text: 'Réorganisez le planning de Louis pour préserver sa lucidité opérationnelle.',
  type: 'propose'
}, {
  text: 'Marie craint de ne pas évoluer, limitant son initiative sur de nouvelles tâches.',
  type: 'revele'
}, {
  text: "Identifiez les freins de Marie pour restaurer sa présence et l'équilibre collectif.",
  type: 'propose'
}, {
  text: "Milo bâcle l'inventaire car il juge cette tâche inutile et mécanique.",
  type: 'revele'
}, {
  text: "Expliquez à Milo l'impact financier d'un stock juste pour fiabiliser ses saisies.",
  type: 'propose'
}];
// Avatar URLs for each person
const AVATARS: Record<string, string> = {
  Marie: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
  Louis: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
  Thomas: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
  Sophie: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
  Milo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
  Alex: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop&crop=face',
  Nadia: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face'
};
// Color gradients that cycle per case (each pair of revele/propose)
const CASE_GRADIENTS = ['from-purple-300 to-pink-300', 'from-blue-300 to-cyan-300', 'from-amber-300 to-yellow-200', 'from-emerald-300 to-teal-300', 'from-rose-300 to-orange-300', 'from-violet-300 to-indigo-300'];
// Recognition Stats Card Component
function RecognitionCard({
  person,
  percentage,
  trend
}: {
  person: string;
  percentage: number;
  trend: number;
}) {
  const avatarUrl = AVATARS[person] || AVATARS.Marie;
  return <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 max-w-[280px] border border-white/30 animate-fade-in" data-id="element-1">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3" data-id="element-2">
        <img src={avatarUrl} alt={person} className="w-10 h-10 rounded-full object-cover border-2 border-rewayz-surface3" data-id="element-3" />
        <div data-id="element-4">
          <p className="text-rewayz-navy text-sm font-semibold" data-id="element-5">{person}</p>
          <p className="text-rewayz-text-secondary text-xs" data-id="element-6">Reconnaissance</p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-end justify-between" data-id="element-7">
        <div data-id="element-8">
          <p className="text-3xl font-bold text-rewayz-navy" data-id="element-9">{percentage}%</p>
          <p className="text-xs text-rewayz-text-muted" data-id="element-10">sur 3 mois</p>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-rewayz-success/10" data-id="element-11">
          <TrendingUp className="w-3.5 h-3.5 text-rewayz-success" data-id="element-12" />
          <span className="text-xs font-semibold text-rewayz-success" data-id="element-13">
            +{trend}%
          </span>
        </div>
      </div>

      {/* Mini chart visualization */}
      <div className="mt-3 flex items-end gap-1 h-8" data-id="element-14">
        <div className="flex-1 bg-rewayz-surface2 rounded-sm h-4" data-id="element-15"></div>
        <div className="flex-1 bg-rewayz-surface2 rounded-sm h-5" data-id="element-16"></div>
        <div className="flex-1 bg-rewayz-violet/60 rounded-sm h-7" data-id="element-17"></div>
        <div className="flex-1 bg-rewayz-violet rounded-sm h-8" data-id="element-18"></div>
      </div>
    </div>;
}
// Floating Icon Component
function FloatingIcon({
  icon: Icon,
  className = '',
  size = 'md'
}: {
  icon: React.ElementType;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClasses = {
    sm: 'w-8 h-8 p-1.5',
    md: 'w-12 h-12 p-2.5',
    lg: 'w-16 h-16 p-3.5'
  };
  return <div className={`rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 ${sizeClasses[size]} ${className}`} data-id="element-19">
      <Icon className="w-full h-full text-white/40" strokeWidth={1.5} data-id="element-20" />
    </div>;
}
// Avatar component for inline display
function InlineAvatar({
  name
}: {
  name: string;
}) {
  const avatarUrl = AVATARS[name] || AVATARS.Thomas;
  return <img src={avatarUrl} alt={name} className="inline-block w-8 h-8 lg:w-10 lg:h-10 rounded-full object-cover mx-1 align-middle border-2 border-purple-300/50" data-id="element-21" />;
}
// Inline Badge Component for highlighted terms
function InlineBadge({
  icon: Icon,
  children
}: {
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-rewayz-amber/40 bg-rewayz-amber/10 mx-2 my-1" data-id="element-22">
      <Icon className="w-3 h-3 text-rewayz-amber flex-shrink-0" data-id="element-23" />
      <span className="text-white text-sm font-medium whitespace-nowrap" data-id="element-24">
        {children}
      </span>
    </span>;
}
// Sparkle burst animation component
function SparkleEffect({
  isActive
}: {
  isActive: boolean;
}) {
  if (!isActive) return null;
  return <span className="relative inline-flex items-center ml-1" data-id="element-25">
      <Sparkles className="w-4 h-4 text-rewayz-amber animate-ping absolute" strokeWidth={2} data-id="element-26" />
      <Sparkles className="w-4 h-4 text-rewayz-amber animate-pulse" strokeWidth={2} data-id="element-27" />
    </span>;
}
export function HeroSection() {
  const [text, setText] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [delta, setDelta] = useState(100);
  const [showProposeEffect, setShowProposeEffect] = useState(false);
  const [prevPromptType, setPrevPromptType] = useState<PromptType>('revele');
  const [showEmailField, setShowEmailField] = useState(false);
  const [showEnterpriseForm, setShowEnterpriseForm] = useState(false);
  const [email, setEmail] = useState('');
  const [enterpriseForm, setEnterpriseForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    entreprise: '',
    poste: '',
    telephone: ''
  });
  const enterpriseFormRef = useRef<HTMLDivElement>(null);
  // Submit states
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [enterpriseSubmitting, setEnterpriseSubmitting] = useState(false);
  const [enterpriseSuccess, setEnterpriseSuccess] = useState(false);
  const [enterpriseError, setEnterpriseError] = useState('');
  // Email signup handler
  const handleEmailSubmit = async () => {
    if (!email || !email.includes('@')) {
      setEmailError('Veuillez entrer un e-mail valide');
      return;
    }
    setEmailSubmitting(true);
    setEmailError('');
    try {
      await insertSignup(email);
      setEmailSuccess(true);
      setEmail('');
      setTimeout(() => {
        setEmailSuccess(false);
        setShowEmailField(false);
      }, 3000);
    } catch (err) {
      setEmailError('Une erreur est survenue. Réessayez.');
      console.error('Signup error:', err);
    } finally {
      setEmailSubmitting(false);
    }
  };
  // Enterprise form handler
  const handleEnterpriseSubmit = async () => {
    if (!enterpriseForm.nom || !enterpriseForm.email || !enterpriseForm.telephone) {
      setEnterpriseError('Veuillez remplir au moins nom, e-mail et téléphone');
      return;
    }
    setEnterpriseSubmitting(true);
    setEnterpriseError('');
    try {
      await insertEnterpriseLead(enterpriseForm);
      setEnterpriseSuccess(true);
      setEnterpriseForm({
        nom: '',
        prenom: '',
        email: '',
        entreprise: '',
        poste: '',
        telephone: ''
      });
      setTimeout(() => {
        setEnterpriseSuccess(false);
        setShowEnterpriseForm(false);
      }, 3000);
    } catch (err) {
      setEnterpriseError('Une erreur est survenue. Réessayez.');
      console.error('Enterprise lead error:', err);
    } finally {
      setEnterpriseSubmitting(false);
    }
  };
  useEffect(() => {
    let ticker = setInterval(() => {
      tick();
    }, delta);
    return () => clearInterval(ticker);
  }, [text, delta]);
  const tick = () => {
    let i = promptIndex % PROMPTS.length;
    let fullText = PROMPTS[i].text;
    let updatedText = isDeleting ? fullText.substring(0, text.length - 1) : fullText.substring(0, text.length + 1);
    setText(updatedText);
    if (isDeleting) {
      setDelta(50);
    }
    if (!isDeleting && updatedText === fullText) {
      setIsDeleting(true);
      setDelta(2000);
    } else if (isDeleting && updatedText === '') {
      setIsDeleting(false);
      setPromptIndex(promptIndex + 1);
      setDelta(100);
    } else if (!isDeleting && updatedText !== fullText) {
      setDelta(50 + Math.random() * 50);
    }
  };
  // Get current prompt type
  const currentPromptType = PROMPTS[promptIndex % PROMPTS.length].type;
  // Detect when verb changes to "propose" and trigger effects
  useEffect(() => {
    if (currentPromptType !== prevPromptType) {
      setPrevPromptType(currentPromptType);
      // Only show effects when switching TO "propose"
      if (currentPromptType === 'propose') {
        setShowProposeEffect(true);
        const timer = setTimeout(() => setShowProposeEffect(false), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [currentPromptType, prevPromptType]);
  // Render text with avatar before names
  const renderTextWithAvatar = () => {
    const names = ['Marie', 'Louis', 'Thomas', 'Sophie', 'Milo', 'Alex', 'Nadia'];
    for (const name of names) {
      const nameIndex = text.indexOf(name);
      if (nameIndex !== -1) {
        const beforeName = text.substring(0, nameIndex);
        const nameAndAfter = text.substring(nameIndex);
        const nameOnly = nameAndAfter.substring(0, name.length);
        const afterName = nameAndAfter.substring(name.length);
        return <>
            {beforeName}
            <InlineAvatar name={name} data-id="element-28" />
            <span className="text-rewayz-yellow" data-id="element-29">{nameOnly}</span>
            {afterName}
          </>;
      }
    }
    return text;
  };
  // Get the gradient for the current case (each pair shares a color)
  const caseIndex = Math.floor(promptIndex % PROMPTS.length / 2);
  const currentGradient = CASE_GRADIENTS[caseIndex % CASE_GRADIENTS.length];
  const inputClasses = 'w-full px-4 py-3 rounded-btn bg-white border border-rewayz-border text-rewayz-navy placeholder-rewayz-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-rewayz-violet focus:border-transparent transition-all duration-300';
  return <section className="relative w-full min-h-screen flex flex-col lg:flex-row overflow-hidden" data-id="element-30">
      {/* Left Side - Navy Background with Typewriter */}
      <div className="w-full lg:w-[65%] bg-rewayz-navy flex flex-col justify-end lg:justify-center px-6 lg:px-16 py-10 pb-20 lg:py-20 lg:pb-20 relative overflow-hidden min-h-screen lg:min-h-0" data-id="element-31">
        {/* Decorative gradient overlay - BEHIND illustrations */}
        <div className="absolute inset-0 bg-gradient-to-br from-rewayz-navy via-rewayz-navy/95 to-rewayz-navy/80 pointer-events-none z-0" data-id="element-32" />

        {/* Decorative Floating Illustrations - ABOVE gradient */}
        <div className="absolute inset-0 pointer-events-none z-[5]" data-id="element-33">
          {/* Top right area */}
          <FloatingIcon icon={Users} size="lg" className="absolute top-20 right-16 transform rotate-12 hidden lg:block" data-id="element-34" />
          <FloatingIcon icon={Heart} size="md" className="absolute top-32 right-48 transform -rotate-6 hidden lg:block" data-id="element-35" />

          {/* Brain - prominent position */}
          <FloatingIcon icon={Brain} size="lg" className="absolute top-24 right-36 transform rotate-3 hidden lg:block" data-id="element-36" />

          {/* Middle right */}
          <FloatingIcon icon={MessageCircle} size="md" className="absolute top-1/3 right-12 transform rotate-6 hidden lg:block" data-id="element-37" />
          <FloatingIcon icon={Target} size="sm" className="absolute top-1/2 right-32 transform -rotate-12 hidden lg:block" data-id="element-38" />

          {/* Curved arrows - trending up */}
          <FloatingIcon icon={TrendingUp} size="md" className="absolute top-[45%] right-8 transform rotate-6 hidden lg:block" data-id="element-39" />
          <FloatingIcon icon={CornerRightUp} size="sm" className="absolute top-[38%] right-44 transform -rotate-3 hidden lg:block" data-id="element-40" />

          {/* Bottom right */}
          <FloatingIcon icon={Zap} size="lg" className="absolute bottom-40 right-20 transform rotate-3 hidden lg:block" data-id="element-41" />
          <FloatingIcon icon={Star} size="sm" className="absolute bottom-28 right-48 transform -rotate-6 hidden lg:block" data-id="element-42" />

          {/* Curved arrows - trending down */}
          <FloatingIcon icon={TrendingDown} size="sm" className="absolute bottom-52 right-12 transform rotate-12 hidden lg:block" data-id="element-43" />
          <FloatingIcon icon={CornerRightDown} size="md" className="absolute bottom-24 right-32 transform -rotate-6 hidden lg:block" data-id="element-44" />

          {/* Left side (partially visible) */}
          <FloatingIcon icon={Heart} size="sm" className="absolute top-28 left-8 transform -rotate-12 hidden lg:block" data-id="element-45" />
          <FloatingIcon icon={Users} size="md" className="absolute bottom-36 left-4 transform rotate-12 hidden lg:block" data-id="element-46" />
          <FloatingIcon icon={Brain} size="sm" className="absolute top-1/2 left-6 transform rotate-6 hidden lg:block" data-id="element-47" />
          <FloatingIcon icon={TrendingUp} size="sm" className="absolute bottom-48 left-12 transform -rotate-3 hidden lg:block" data-id="element-48" />

          {/* Decorative circles */}
          <div className="absolute top-1/4 right-8 w-32 h-32 rounded-full border-2 border-white/10 hidden lg:block" data-id="element-49" />
          <div className="absolute bottom-1/4 right-24 w-48 h-48 rounded-full border-2 border-white/10 hidden lg:block" data-id="element-50" />
          <div className="absolute top-1/2 right-40 w-24 h-24 rounded-full bg-rewayz-violet/10 hidden lg:block" data-id="element-51" />

          {/* Mobile-only floating icons (smaller, fewer, subtle) */}
          <FloatingIcon icon={Heart} size="sm" className="absolute top-12 right-6 transform rotate-12 lg:hidden opacity-80" data-id="element-52" />
          <FloatingIcon icon={Brain} size="sm" className="absolute top-20 left-4 transform -rotate-6 lg:hidden opacity-80" data-id="element-53" />
          <FloatingIcon icon={Users} size="sm" className="absolute top-1/3 right-4 transform rotate-6 lg:hidden opacity-80" data-id="element-54" />
          <FloatingIcon icon={Star} size="sm" className="absolute top-[45%] left-6 transform -rotate-12 lg:hidden opacity-80" data-id="element-55" />
          <FloatingIcon icon={TrendingUp} size="sm" className="absolute top-[55%] right-8 transform rotate-3 lg:hidden opacity-80" data-id="element-56" />
          <FloatingIcon icon={Zap} size="sm" className="absolute bottom-48 left-4 transform rotate-6 lg:hidden opacity-80" data-id="element-57" />
          <div className="absolute top-1/4 right-4 w-20 h-20 rounded-full border border-white/15 lg:hidden" data-id="element-58" />
          <div className="absolute bottom-1/3 left-2 w-16 h-16 rounded-full border border-white/15 lg:hidden" data-id="element-59" />
        </div>

        {/* Main Content */}
        <div className="max-w-xl mx-auto w-full relative z-10 flex flex-col h-full lg:h-full justify-between lg:justify-between py-4 lg:py-8 gap-0 lg:gap-0 flex-1" data-id="element-60">
          {/* Top section - Logo phrase and typewriter */}
          <div data-id="element-61">
            {/* Dynamic phrase with Logo */}
            <div className="flex items-center gap-1.5 lg:gap-2 mb-3 lg:mb-6 flex-wrap" data-id="element-62">
              <span className="text-white/70 text-sm lg:text-lg font-medium" data-id="element-63">
                Voilà ce que
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/vvKPgh6ZyT6sqTxcDq83VU/logo_rewayz_white.png" alt="Rewayz" className="h-4 lg:h-6 w-auto" data-id="element-64" />
              <span className="inline-flex items-center" data-id="element-65">
                <span key={currentPromptType} className={`text-sm lg:text-lg font-semibold transition-all duration-300 ${currentPromptType === 'revele' ? 'text-white' : 'text-rewayz-yellow'}`} data-id="element-66">
                  {currentPromptType === 'revele' ? 'révèle' : 'propose'}
                </span>
                <SparkleEffect isActive={showProposeEffect && currentPromptType === 'propose'} data-id="element-67" />
              </span>
            </div>

            <div className="min-h-[90px] lg:min-h-[120px]" data-id="element-68">
              <h1 className="text-2xl lg:text-4xl font-semibold text-white leading-tight" data-id="element-69">
                <span className={`bg-clip-text text-transparent bg-gradient-to-r ${currentGradient} transition-all duration-500`} data-id="element-70">
                  {renderTextWithAvatar()}
                </span>
                <span className="animate-pulse text-rewayz-violet ml-1" data-id="element-71">|</span>
              </h1>
            </div>
          </div>

          {/* Bottom section - Features and logos */}
          <div className="max-w-md pb-0 lg:pb-8" data-id="element-72">
            <p className="text-white/80 text-sm mb-3" data-id="element-73">
              L'outil intelligent{' '}
              <span className="font-semibold text-white" data-id="element-74">
                pour les managers
              </span>{' '}
              qui :
            </p>
            <div className="space-y-2" data-id="element-75">
              <div className="flex items-center gap-2" data-id="element-76">
                <Activity className="w-4 h-4 text-rewayz-amber" data-id="element-77" />
                <span className="text-white text-sm" data-id="element-78">
                  Révèle{' '}
                  <span className="text-rewayz-amber font-medium" data-id="element-79">
                    l'engagement réel
                  </span>{' '}
                  de vos équipes
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-80">
                <AlertTriangle className="w-4 h-4 text-rewayz-amber" data-id="element-81" />
                <span className="text-white text-sm" data-id="element-82">
                  Identifie leurs{' '}
                  <span className="text-rewayz-amber font-medium" data-id="element-83">
                    points de friction
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-84">
                <Lightbulb className="w-4 h-4 text-rewayz-amber" data-id="element-85" />
                <span className="text-white text-sm" data-id="element-86">
                  Vous guide vers les{' '}
                  <span className="text-rewayz-amber font-medium" data-id="element-87">
                    bonnes décisions
                  </span>
                </span>
              </div>
            </div>

            {/* Trust logos section */}
            <div className="mt-6 lg:mt-8 pt-4 lg:pt-6 border-t border-white/10" data-id="element-88">
              <p className="text-white/50 text-xs mb-3 lg:mb-4 uppercase tracking-wide" data-id="element-89">
                Les managers qui nous font déjà confiance
              </p>
              <div className="flex gap-3 lg:gap-6 mb-[20px] flex-row items-center justify-between w-full" data-id="element-90">
                <img src="https://cdn.magicpatterns.com/uploads/qVDayf3WHpJVRoWeG6VnbA/logo-leclerc.png" alt="E.Leclerc" className="h-8 lg:h-14 w-auto max-w-[18%] object-contain opacity-70 hover:opacity-100 transition-opacity" data-id="element-91" />
                <img src="https://cdn.magicpatterns.com/uploads/c5QCxaiYjctKswYq1rqRNo/image_(16)_1.png" alt="TotalEnergies" className="h-8 lg:h-14 w-auto max-w-[18%] object-contain opacity-70 hover:opacity-100 transition-opacity" data-id="element-92" />
                <img src="https://cdn.magicpatterns.com/uploads/6TU12NpPSmENwdsNKo2iGC/logo-french-bastard.png" alt="French Bastards" className="h-8 lg:h-14 w-auto max-w-[18%] object-contain opacity-70 hover:opacity-100 transition-opacity" data-id="element-93" />
                <img src="https://cdn.magicpatterns.com/uploads/8cCEC2Li7ij35LMq56TwtS/image_(17)_1.png" alt="Qualimétrie" className="h-8 lg:h-14 w-auto max-w-[18%] object-contain opacity-70 hover:opacity-100 transition-opacity" data-id="element-94" />
                <img src="https://cdn.magicpatterns.com/uploads/iHdDAHqBN27KuEuJDEgU2b/image_(22)_1.png" alt="Burger King" className="h-8 lg:h-14 w-auto max-w-[18%] object-contain opacity-70 hover:opacity-100 transition-opacity" data-id="element-95" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-rewayz-navy to-transparent pointer-events-none z-20" data-id="element-96" />
      </div>

      {/* Right Side - Black/Dark Background with Actions */}
      <div style={{
      position: 'fixed'
    } as React.CSSProperties} className="bottom-0 left-0 right-0 z-50 lg:!relative lg:z-auto lg:overflow-visible w-full lg:w-[35%] bg-black flex flex-col justify-center items-center px-4 lg:px-16 py-3 lg:py-20 lg:relative" data-id="element-97">
        <div className="max-w-md w-full text-center lg:text-left" data-id="element-98">
          <h2 className="hidden lg:block text-3xl lg:text-4xl font-bold text-white mb-12 text-center" data-id="element-99">
            Démarrer
          </h2>

          {/* Inscription facile → Email field */}
          <div className={`flex flex-col gap-2 justify-center items-center w-full mb-0 lg:mb-8 transition-all duration-500 ease-in-out ${showEnterpriseForm ? 'opacity-0 scale-95 h-0 overflow-hidden pointer-events-none mb-0' : 'opacity-100 scale-100'}`} data-id="element-100">
            <div className="relative w-full sm:w-auto flex justify-center" data-id="element-101">
              {/* Button state */}
              <div className={`transition-all duration-500 ease-in-out flex flex-col items-center ${showEmailField ? 'opacity-0 scale-95 absolute pointer-events-none' : 'opacity-100 scale-100'}`} data-id="element-102">
                {/* Mobile: side by side layout */}
                <div className="flex lg:hidden flex-col items-center gap-2 w-full" data-id="element-103">
                  <div className="flex items-center gap-3 w-full" data-id="element-104">
                    <div className="flex flex-col items-center flex-1" data-id="element-105">
                      <button onClick={() => {
                      setShowEmailField(true);
                      setShowEnterpriseForm(false);
                    }} className="w-full px-5 py-2.5 rounded-btn bg-rewayz-violet text-white font-semibold text-sm hover:bg-blue-600 transition-colors" data-id="element-106">
                        Inscription facile
                      </button>
                      <p className="text-white/50 text-[10px] mt-1" data-id="element-107">
                        pour mon équipe
                      </p>
                    </div>
                    <a href="#" onClick={e => {
                    e.preventDefault();
                    setShowEnterpriseForm(true);
                    setShowEmailField(false);
                    setEmail('');
                    setTimeout(() => {
                      enterpriseFormRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                      });
                    }, 100);
                  }} className="text-rewayz-surface3/80 hover:text-white transition-colors text-[11px] text-center leading-tight flex items-center gap-1 group max-w-[120px]" data-id="element-108">
                      <span data-id="element-109">Essayer pour toute mon entreprise</span>
                      <ArrowRight className="w-3 h-3 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" data-id="element-110" />
                    </a>
                  </div>
                </div>

                {/* Desktop: original stacked layout */}
                <div className="hidden lg:flex flex-col items-center" data-id="element-111">
                  <button onClick={() => {
                  setShowEmailField(true);
                  setShowEnterpriseForm(false);
                }} className="w-full sm:w-auto px-8 py-3 rounded-btn bg-rewayz-violet text-white font-semibold hover:bg-blue-600 transition-colors focus:ring-2 focus:ring-rewayz-violet focus:ring-offset-2 focus:ring-offset-black" data-id="element-112">
                    Inscription facile
                  </button>
                  <p className="text-white/50 text-xs mt-2" data-id="element-113">pour mon équipe</p>
                </div>
              </div>

              {/* Email field state */}
              <div className={`transition-all duration-500 ease-in-out w-full max-w-sm ${showEmailField ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute pointer-events-none'}`} data-id="element-114">
                {emailSuccess ? <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-btn bg-green-500/20 border border-green-500/30" data-id="element-115">
                    <Check className="w-4 h-4 text-green-400" data-id="element-116" />
                    <span className="text-green-400 text-sm font-medium" data-id="element-117">
                      Vérifiez votre boite e-mail !
                    </span>
                  </div> : <>
                    <div className="relative flex items-center gap-2" data-id="element-118">
                      <div className="relative flex-1" data-id="element-119">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rewayz-text-muted" data-id="element-120" />
                        <input type="email" placeholder="E-mail" value={email} onChange={e => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }} onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()} autoFocus={showEmailField} className={`${inputClasses} pl-10 pr-12 ${emailError ? 'border-red-400' : ''}`} data-id="element-121" />
                        <button onClick={handleEmailSubmit} disabled={emailSubmitting} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-rewayz-amber flex items-center justify-center hover:bg-amber-400 transition-colors disabled:opacity-50" aria-label="Envoyer" data-id="element-122">
                          {emailSubmitting ? <Loader2 className="w-3.5 h-3.5 text-rewayz-navy animate-spin" data-id="element-123" /> : <Send className="w-3.5 h-3.5 text-rewayz-navy" data-id="element-124" />}
                        </button>
                      </div>
                      <button onClick={() => {
                    setShowEmailField(false);
                    setEmail('');
                    setEmailError('');
                  }} className="w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all flex-shrink-0" aria-label="Fermer" data-id="element-125">
                        <X className="w-4 h-4" data-id="element-126" />
                      </button>
                    </div>
                    {emailError && <p className="text-red-400 text-xs mt-2 text-center" data-id="element-127">
                        {emailError}
                      </p>}
                  </>}
              </div>
            </div>
          </div>

          {/* Essayez pour toute mon entreprise → Full form */}
          <div className="mt-0 lg:mt-[80px]" data-id="element-128">
            {/* Link state - hidden on mobile (integrated in sticky bar above) */}
            <div className={`text-center transition-all duration-500 ease-in-out hidden lg:block ${showEnterpriseForm ? 'lg:opacity-0 lg:h-0 lg:overflow-hidden lg:pointer-events-none' : 'lg:opacity-100'}`} data-id="element-129">
              <a href="#" onClick={e => {
              e.preventDefault();
              setShowEnterpriseForm(true);
              setShowEmailField(false);
              setEmail('');
            }} className="text-rewayz-surface3/80 hover:text-white transition-colors text-sm flex items-center justify-center gap-2 group" data-id="element-130">
                Essayez pour toute mon entreprise
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-131" />
              </a>
            </div>

            {/* Enterprise form state */}
            <div ref={enterpriseFormRef} className={`transition-all duration-500 ease-in-out origin-top ${showEnterpriseForm ? 'opacity-100 scale-100 max-h-[600px]' : 'opacity-0 scale-95 max-h-0 overflow-hidden pointer-events-none'}`} data-id="element-132">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-rewayz-border p-4 lg:p-6 shadow-lg max-h-[70vh] lg:max-h-none overflow-y-auto" data-id="element-133">
                <div className="flex items-start justify-between mb-5" data-id="element-134">
                  <div data-id="element-135">
                    <h3 className="text-rewayz-navy font-semibold text-base" data-id="element-136">
                      Rewayz pour mon entreprise
                    </h3>
                    <p className="text-rewayz-text-muted text-xs mt-1" data-id="element-137">
                      Nous vous recontactons sous 24h
                    </p>
                  </div>
                  <button onClick={() => setShowEnterpriseForm(false)} className="w-7 h-7 rounded-full flex items-center justify-center text-rewayz-text-muted hover:text-rewayz-navy hover:bg-rewayz-surface2 transition-all flex-shrink-0" aria-label="Fermer" data-id="element-138">
                    <X className="w-4 h-4" data-id="element-139" />
                  </button>
                </div>

                <div className="space-y-3" data-id="element-140">
                  {/* Nom + Prénom row */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-141">
                    <input type="text" placeholder="Nom" value={enterpriseForm.nom} onChange={e => setEnterpriseForm({
                    ...enterpriseForm,
                    nom: e.target.value
                  })} className={inputClasses} data-id="element-142" />
                    <input type="text" placeholder="Prénom" value={enterpriseForm.prenom} onChange={e => setEnterpriseForm({
                    ...enterpriseForm,
                    prenom: e.target.value
                  })} className={inputClasses} data-id="element-143" />
                  </div>

                  {/* Email */}
                  <div className="relative" data-id="element-144">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rewayz-text-muted" data-id="element-145" />
                    <input type="email" placeholder="E-mail" value={enterpriseForm.email} onChange={e => setEnterpriseForm({
                    ...enterpriseForm,
                    email: e.target.value
                  })} className={`${inputClasses} pl-10`} data-id="element-146" />
                  </div>

                  {/* Entreprise */}
                  <input type="text" placeholder="Entreprise" value={enterpriseForm.entreprise} onChange={e => setEnterpriseForm({
                  ...enterpriseForm,
                  entreprise: e.target.value
                })} className={inputClasses} data-id="element-147" />

                  {/* Poste (select) */}
                  <div className="relative" data-id="element-148">
                    <select value={enterpriseForm.poste} onChange={e => setEnterpriseForm({
                    ...enterpriseForm,
                    poste: e.target.value
                  })} className={`${inputClasses} appearance-none cursor-pointer ${!enterpriseForm.poste ? 'text-rewayz-text-muted' : 'text-rewayz-navy'}`} data-id="element-149">
                      <option value="" disabled data-id="element-150">
                        Poste
                      </option>
                      <option value="directeur" data-id="element-151">Directeur</option>
                      <option value="rh" data-id="element-152">RH</option>
                      <option value="autres" data-id="element-153">Autres</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rewayz-text-muted pointer-events-none" data-id="element-154" />
                  </div>

                  {/* Téléphone */}
                  <div className="relative" data-id="element-155">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rewayz-text-muted" data-id="element-156" />
                    <input type="tel" placeholder="Téléphone" value={enterpriseForm.telephone} onChange={e => setEnterpriseForm({
                    ...enterpriseForm,
                    telephone: e.target.value
                  })} className={`${inputClasses} pl-10`} data-id="element-157" />
                  </div>
                </div>

                {/* Submit */}
                {enterpriseSuccess ? <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-btn bg-rewayz-success/10 border border-rewayz-success/30 mt-5" data-id="element-158">
                    <Check className="w-4 h-4 text-rewayz-success" data-id="element-159" />
                    <span className="text-rewayz-success text-sm font-medium" data-id="element-160">
                      Nous revenons vers vous dans moins de 24h !
                    </span>
                  </div> : <>
                    {enterpriseError && <p className="text-red-500 text-xs mt-3 text-center" data-id="element-161">
                        {enterpriseError}
                      </p>}
                    <button onClick={handleEnterpriseSubmit} disabled={enterpriseSubmitting} className="w-full mt-5 px-6 py-3 rounded-btn bg-rewayz-amber text-rewayz-navy font-semibold hover:bg-amber-400 transition-colors focus:ring-2 focus:ring-rewayz-amber focus:ring-offset-2 focus:ring-offset-white text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-162">
                      {enterpriseSubmitting ? <>
                          <Loader2 className="w-4 h-4 animate-spin" data-id="element-163" />
                          Envoi...
                        </> : 'Envoyer'}
                    </button>
                  </>}
              </div>
            </div>
          </div>

          {/* Legal links */}
          <div className="hidden lg:flex absolute bottom-6 left-0 right-0 justify-center items-center gap-4" data-id="element-164">
            <span className="text-rewayz-surface3/50 text-xs flex items-center gap-1 leading-none" data-id="element-165">
              <span className="text-sm leading-none" data-id="element-166">🇫🇷</span> Rewayz 2026
            </span>
            <span className="text-rewayz-surface3/30 leading-none" data-id="element-167">•</span>
            <a href="#" className="text-rewayz-surface3/50 hover:text-rewayz-surface3/80 transition-colors text-xs leading-none" data-id="element-168">
              Politique de confidentialité
            </a>
          </div>
        </div>
      </div>
    </section>;
}